import apiFetch from '../../common/apiFetch';
import { logError, base64ToBlob, getImageDimensions } from './utils';
import { ImageResult } from './eraseContent';

export interface GenerateFromUrlParams {
	url: string
	userPrompt: string
	negative_prompt?: string
	aspectRatio: '1.91:1' | '1:1' | '3:1'
	seed?: number
	output_format?: 'png'
	style_preset?: 'photographic'
	isPromptOnly?: boolean
}

interface GenerateFromUrlResponse {
	status: 'success'
	image: string
	generated_prompt?: string
	timestamp?: number
}

export const generateFromUrl = async (params: GenerateFromUrlParams): Promise<ImageResult> => {
	let objectUrl: string | null = null;

	try {
		const response = await apiFetch('/ai-image-editor/generate-from-url', {
			method: 'POST',
			headers: {
				contentType: 'application/json',
				accept: 'application/json'
			},
			body: JSON.stringify({
				url: params.url,
				userPrompt: params.userPrompt,
				negative_prompt: params.negative_prompt,
				aspectRatio: params.aspectRatio,
				seed: params.seed,
				output_format: params.output_format ?? 'png',
				style_preset: params.style_preset ?? 'photographic',
				isPromptOnly: params.isPromptOnly ?? false
			})
		});

		if (!response.ok) {
			const errorText = await response.text();
			throw new Error(`Failed to generate image from URL: ${response.status} ${response.statusText} - ${errorText}`);
		}

		// Check if response is JSON or binary
		const contentType = response.headers.get('content-type');
		let resultBlob: Blob;

		if (contentType && contentType.includes('application/json')) {
			// Handle JSON response
			const result = (await response.json()) as GenerateFromUrlResponse;

			if (!result.image) {
				throw new Error('No image data received from server');
			}

			// Convert base64 to blob
			try {
				resultBlob = base64ToBlob(result.image);
			} catch (error) {
				logError('Error converting base64 to blob:', error);
				throw new Error('Failed to process image data from server');
			}
		} else {
			// Handle binary response
			resultBlob = await response.blob();
		}

		objectUrl = URL.createObjectURL(resultBlob);

		// Get dimensions from the generated image
		const dimensions = await getImageDimensions(objectUrl);

		return {
			url: objectUrl,
			width: dimensions.width,
			height: dimensions.height,
			cleanup: () => {
				if (objectUrl) {
					URL.revokeObjectURL(objectUrl);
					objectUrl = null;
				}
			}
		};
	} catch (error) {
		logError('Error in generateFromUrl:', error);
		if (objectUrl) {
			URL.revokeObjectURL(objectUrl);
		}
		throw error;
	}
};

/**
 * Helper function to normalize image URLs
 */
export const getImageUrl = (url: string): string => {
	return url.startsWith('http') || url.startsWith('blob:')
		? url
		: url.startsWith('/')
			? url
			: `/${url}`;
}
